import React, { useState } from 'react';
import axios from 'axios';
import { WINNING_CHANCE, DEAL_STATUSES, STATUS_EMOJI, CHANCE_LABEL, VENUES } from '../constants/meetingOptions';
import { sumTickets } from './meeting/metrics';
import './MeetingDealTable.css';
import { money0 } from '../utils/money';

const API_BASE = '/api';
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
// "2026 November" -> "26'November": the century says nothing (every deal is 20xx)
// and the column is width-starved next to full venue names.
const shortMonth = (v) => String(v || '').replace(/^20(\d{2})\s+/, "$1'");

const EMPTY = { client_name: '', event_month: '', ticket_size: '', winning_chance: '', statuses: [], deal_details: '', venues: [] };
// A deal's `venues` mixes two kinds of entry: the six W-brand codes, and any number
// of typed names for event spaces the company does not run. Codes come first in
// VENUES order, typed names after — the exact order the backend's sanitizeVenues
// stores, so equality checks stay a plain join.
const isVenueCode = (v) => VENUES.some(x => x.code === v);
// The six house venues are STORED as codes but never shown as one: "WT" means nothing
// to anyone outside the team, so every cell and every checkbox reads "W Timber". A
// typed venue has no code and renders as itself.
const venueLabel = (v) => { const m = VENUES.find(x => x.code === v); return m ? m.label : v; };
const codesOf = (arr) => (arr || []).filter(isVenueCode);
const typedOf = (arr) => (arr || []).filter(v => !isVenueCode(v));
// Toggle a venue code in/out of a selection without disturbing the typed names.
const toggleVenue = (arr, code) => {
  const codes = arr.includes(code)
    ? codesOf(arr).filter(c => c !== code)
    : VENUES.filter(v => arr.includes(v.code) || v.code === code).map(v => v.code);
  return [...codes, ...typedOf(arr)];
};
const sameVenues = (a, b) => (a || []).join(',') === (b || []).join(',');
// Every field is required to add/save a deal — except Details.
const REQUIRED_FIELDS = [
  { key: 'client_name', label: 'Client' },
  { key: 'event_month', label: 'Month' },
  { key: 'ticket_size', label: 'Ticket Size' },
  { key: 'winning_chance', label: 'Chance' },
  { key: 'statuses', label: 'Status' },
];
const rm = v => (v == null || v === '' ? '' : 'RM ' + Number(v).toLocaleString('en-MY', { minimumFractionDigits: 0 }));
// Slug a chance/status value into a CSS modifier (e.g. "Done Deal" -> "done-deal").
const slug = s => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
// Status label with its optional emoji prefix (display only; the stored/selected value stays plain).
const statusLabel = s => (STATUS_EMOJI[s] ? `${STATUS_EMOJI[s]} ${s}` : s);
// Winning-chance short display label (display only; the stored/selected value stays the full string).
const chanceLabel = s => CHANCE_LABEL[s] || s;
// Sort key: the deal's status position in the pipeline; no/unknown status sinks to the bottom.
const statusRank = d => {
  const i = DEAL_STATUSES.indexOf((d.statuses || [])[0]);
  return i === -1 ? DEAL_STATUSES.length : i;
};

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];
const YEARS = [2026, 2027, 2028, 2029, 2030];
const composeMonth = (year, month) => [year, month].filter(Boolean).join(' ');
// "2026 July" (any token order) -> { year: '2026', month: 'July' }
const parseMonth = (str) => {
  let year = '', month = '';
  for (const t of String(str || '').trim().split(/\s+/)) {
    if (/^\d{4}$/.test(t)) year = t;
    else { const m = MONTHS.find(mm => mm.toLowerCase() === t.toLowerCase()); if (m) month = m; }
  }
  return { year, month };
};

// Venue multi-select — a deal can span venues (e.g. "Teoh (WF, WS)"), unlike every
// other cell in this table which holds one value. The six W-brand codes are
// checkboxes; "Other" is a free-text box for an event space the company does not
// run, stored in the same array as the codes.
//
// A typed name is a RECORD, never a metric: services/scorecardFigures.js counts the
// six codes and nothing else, so writing "Spacehub" here never adds an event-space
// done deal. That is deliberate — see the note there.
//
// When onCommit is given, it fires once after focus leaves the whole group (checkbox
// to text box and back does not commit), mirroring MonthYearSelect's commit-on-blur.
function VenueEditor({ value, autoFocus, onChange, onCommit }) {
  const typed = typedOf(value);
  const handleBlur = onCommit
    ? (e) => { const w = e.currentTarget; setTimeout(() => { if (!w.contains(document.activeElement)) onCommit(); }, 0); }
    : undefined;
  // The box edits the FIRST typed name; clearing it removes that name. Any further
  // typed names (only reachable via the API, which allows several) are carried
  // through untouched rather than silently dropped by an edit here.
  //
  // Typing a HOUSE venue's name ("W Timber") ticks that venue instead of storing
  // text: the sanitiser drops free text that restates a code, so keeping it as text
  // would save nothing and the name would silently vanish on reload.
  const setTyped = (text) => {
    const codes = codesOf(value);
    const house = VENUES.find(v => v.label.toLowerCase() === text.trim().toLowerCase());
    if (house) {
      onChange([...(codes.includes(house.code) ? codes : [...codes, house.code]), ...typed.slice(1)]);
      return;
    }
    onChange([...codes, ...(text.trim() ? [text] : []), ...typed.slice(1)]);
  };
  return (
    <div className="mdt-venue-edit" onBlur={handleBlur}>
      {VENUES.map((v, i) => (
        <label key={v.code} className="mdt-venue-opt">
          <input
            type="checkbox"
            autoFocus={autoFocus && i === 0}
            checked={value.includes(v.code)}
            onChange={() => onChange(toggleVenue(value, v.code))}
          />
          {v.label}
        </label>
      ))}
      <div className="mdt-venue-other">
        <span>Other</span>
        <input
          type="text"
          className="mdt-in mdt-venue-otherin"
          aria-label="Other venue"
          placeholder="Type the venue name"
          maxLength={60}
          value={typed[0] || ''}
          onChange={e => setTyped(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
        />
      </div>
      <p className="mdt-venue-note">
        Only the six W venues count on the scorecard. An outside venue is recorded
        on the deal, not in the figures.
      </p>
    </div>
  );
}

// Month + Year dropdowns (years 2026–2030). Controlled via the composed "YYYY Month" string.
// When onCommit is given, fires once after focus leaves both selects (for inline cell editing).
function MonthYearSelect({ value, autoFocus, onChange, onCommit, invalid }) {
  const { year, month } = parseMonth(value);
  const cls = 'mdt-in' + (invalid ? ' mdt-invalid' : '');
  const handleBlur = onCommit
    ? (e) => { const w = e.currentTarget; setTimeout(() => { if (!w.contains(document.activeElement)) onCommit(); }, 0); }
    : undefined;
  return (
    <div className="mdt-monthyear" onBlur={handleBlur}>
      <select className={cls} autoFocus={autoFocus} value={month} onChange={e => onChange(composeMonth(year, e.target.value))}>
        <option value="">Month…</option>
        {MONTHS.map(m => <option key={m} value={m}>{m}</option>)}
      </select>
      <select className={cls} value={year} onChange={e => onChange(composeMonth(e.target.value, month))}>
        <option value="">Year…</option>
        {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
      </select>
    </div>
  );
}

export default function MeetingDealTable({ meetingId, salespersonId, deals, editable, onChange }) {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [cell, setCell] = useState(null);   // { id, field } currently being edited
  const [cellVal, setCellVal] = useState('');
  const [error, setError] = useState('');
  const [invalid, setInvalid] = useState([]); // required add-form fields left empty (shown red)

  const base = `${API_BASE}/meetings/${meetingId}/sections/${salespersonId}/deals`;
  const cols = editable ? 8 : 7;
  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setInvalid(inv => (inv.length ? inv.filter(x => x !== k) : inv)); };

  const startAdd = () => { setError(''); setInvalid([]); setForm(EMPTY); setCell(null); setAdding(true); };
  const cancelAdd = () => { setAdding(false); setForm(EMPTY); setError(''); setInvalid([]); };

  const addDeal = async () => {
    setError('');
    const missing = REQUIRED_FIELDS.filter(f => !String((f.key === 'statuses' ? form.statuses[0] : form[f.key]) ?? '').trim());
    if (missing.length) { setInvalid(missing.map(f => f.key)); return; } // red boxes mark the gaps
    setInvalid([]);
    try { await axios.post(base, form, { headers: headers() }); cancelAdd(); onChange(); }
    catch (e) { setError(e.response?.data?.error || 'Save failed'); }
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete deal "${d.client_name}"?`)) return;
    try { await axios.delete(`${base}/${d.id}`, { headers: headers() }); onChange(); }
    catch (e) { setError(e.response?.data?.error || 'Delete failed'); }
  };

  // ---- Single-click, single-field inline editing ----
  const isEditing = (d, field) => cell && cell.id === d.id && cell.field === field;
  const openCell = (d, field) => {
    if (!editable) return;
    setError(''); setAdding(false);
    setCell({ id: d.id, field });
    setCellVal(
      field === 'statuses' ? (d.statuses?.[0] || '')
        : field === 'venues' ? (d.venues || [])
        : (d[field] ?? '')
    );
  };
  const cancelCell = () => { setCell(null); setCellVal(''); };
  // Close only if this exact cell is still the active one (avoids clobbering a freshly-opened cell).
  const closeIfCurrent = (d, field) => setCell(c => (c && c.id === d.id && c.field === field ? null : c));

  const saveCell = async (d, field, value) => {
    const reqd = REQUIRED_FIELDS.find(f => f.key === field);
    const empty = field === 'statuses' ? !value : !String(value ?? '').trim();
    if (reqd && empty) { setError(`${reqd.label} can’t be empty.`); cancelCell(); return; }
    const orig = field === 'statuses' ? (d.statuses?.[0] || '')
      : field === 'venues' ? (d.venues || [])
      : (d[field] ?? '');
    const noop = field === 'venues' ? sameVenues(value, orig) : String(value ?? '') === String(orig ?? '');
    if (noop) { closeIfCurrent(d, field); return; } // no-op
    // The PUT replaces the deal's full field set rather than merging, so every
    // save — even one editing an unrelated cell — must resend venues or it gets cleared.
    const body = {
      client_name: d.client_name, event_month: d.event_month, ticket_size: d.ticket_size,
      winning_chance: d.winning_chance, statuses: d.statuses || [], deal_details: d.deal_details,
      venues: d.venues || [],
      [field]: field === 'statuses' ? (value ? [value] : []) : value
    };
    try {
      await axios.put(`${base}/${d.id}`, body, { headers: headers() });
      closeIfCurrent(d, field); onChange();
    } catch (e) { setError(e.response?.data?.error || 'Save failed'); closeIfCurrent(d, field); }
  };

  const textEditor = (d, field, placeholder, numeric) => (
    <input
      className="mdt-in" autoFocus value={cellVal} placeholder={placeholder}
      inputMode={numeric ? 'decimal' : undefined}
      onChange={e => setCellVal(numeric ? e.target.value.replace(/[^0-9.]/g, '') : e.target.value)}
      onBlur={() => saveCell(d, field, cellVal)}
      onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); else if (e.key === 'Escape') cancelCell(); }}
    />
  );
  const selectEditor = (d, field, options, placeholder, labelFn = statusLabel) => (
    <select className="mdt-in" autoFocus value={cellVal} onChange={e => saveCell(d, field, e.target.value)} onBlur={cancelCell}>
      <option value="">{placeholder}</option>
      {options.map(o => <option key={o} value={o}>{labelFn(o)}</option>)}
    </select>
  );

  const cellProps = (d, field, extra) => ({
    className: 'mdt-cell' + (extra ? ' ' + extra : ''),
    onClick: editable && !isEditing(d, field) ? () => openCell(d, field) : undefined,
    title: editable && !isEditing(d, field) ? 'Click to edit' : undefined,
  });

  const inCls = (k) => 'mdt-in' + (invalid.includes(k) ? ' mdt-invalid' : '');
  const addCells = () => (
    <>
      <td><input className={inCls('client_name')} placeholder="Client name" value={form.client_name} onChange={e => set('client_name', e.target.value)} /></td>
      <td><VenueEditor value={form.venues} onChange={v => set('venues', v)} /></td>
      <td><MonthYearSelect value={form.event_month} invalid={invalid.includes('event_month')} onChange={v => set('event_month', v)} /></td>
      <td><input className={inCls('ticket_size')} placeholder="RM" inputMode="decimal" value={form.ticket_size} onChange={e => set('ticket_size', e.target.value.replace(/[^0-9.]/g, ''))} /></td>
      <td>
        <select className={inCls('winning_chance')} value={form.winning_chance} onChange={e => set('winning_chance', e.target.value)}>
          <option value="">Chance…</option>
          {WINNING_CHANCE.map(w => <option key={w} value={w}>{chanceLabel(w)}</option>)}
        </select>
      </td>
      <td>
        <select className={inCls('statuses')} value={form.statuses[0] || ''} onChange={e => set('statuses', e.target.value ? [e.target.value] : [])}>
          <option value="">Status…</option>
          {DEAL_STATUSES.map(s => <option key={s} value={s}>{statusLabel(s)}</option>)}
        </select>
      </td>
      <td><input className="mdt-in" placeholder="Details (any language)" value={form.deal_details} onChange={e => set('deal_details', e.target.value)} /></td>
      <td className="mdt-rowactions">
        <button className="mdt-link save" onClick={addDeal}>Add</button>
        <button className="mdt-link" onClick={cancelAdd}>Cancel</button>
      </td>
    </>
  );

  // Display deals ordered by pipeline stage (stable within a status; no-status last).
  const sortedDeals = [...(deals || [])].sort((a, b) => statusRank(a) - statusRank(b));

  const ticketTotal = sumTickets(sortedDeals);

  return (
    <div className="mdt">
      {error && <div className="mdt-error">{error}</div>}
      <table className={'mdt-table' + (editable ? ' editable' : '')}>
        <thead>
          <tr><th>Client</th><th>Venue</th><th>Month</th><th>Ticket</th><th>Chance</th><th>Status</th><th>Details</th>{editable && <th></th>}</tr>
        </thead>
        <tbody>
          {sortedDeals.map(d => (
            <tr key={d.id}>
              <td {...cellProps(d, 'client_name')}>
                {isEditing(d, 'client_name') ? textEditor(d, 'client_name', 'Client name') : d.client_name}
              </td>
              <td {...cellProps(d, 'venues')}>
                {isEditing(d, 'venues')
                  ? <VenueEditor value={cellVal} autoFocus onChange={setCellVal} onCommit={() => saveCell(d, 'venues', cellVal)} />
                  : (d.venues || []).map(v => (
                    <span key={v} className={'mdt-venuetag' + (isVenueCode(v) ? '' : ' typed')}>
                      {venueLabel(v)}
                    </span>
                  ))}
              </td>
              <td {...cellProps(d, 'event_month')}>
                {isEditing(d, 'event_month')
                  ? <MonthYearSelect value={cellVal} autoFocus onChange={setCellVal} onCommit={() => saveCell(d, 'event_month', cellVal)} />
                  : shortMonth(d.event_month)}
              </td>
              <td {...cellProps(d, 'ticket_size')}>
                {isEditing(d, 'ticket_size') ? textEditor(d, 'ticket_size', 'RM', true) : (
                  d.ticket_size != null && d.ticket_size !== '' ? (
                    <span className="mdt-ticket-fig">{rm(d.ticket_size)}</span>
                  ) : null
                )}
              </td>
              <td {...cellProps(d, 'winning_chance')}>
                {isEditing(d, 'winning_chance')
                  ? selectEditor(d, 'winning_chance', WINNING_CHANCE, 'Chance…', chanceLabel)
                  : (d.winning_chance ? <span className={`mdt-chance ch-${slug(d.winning_chance)}`}>{chanceLabel(d.winning_chance)}</span> : '')}
              </td>
              <td {...cellProps(d, 'statuses')}>
                {isEditing(d, 'statuses')
                  ? selectEditor(d, 'statuses', DEAL_STATUSES, 'Status…')
                  : (d.statuses || []).map(s => <span key={s} className={`mdt-tag st-${slug(s)}`}>{statusLabel(s)}</span>)}
              </td>
              <td {...cellProps(d, 'deal_details', 'mdt-details')}>
                {isEditing(d, 'deal_details') ? textEditor(d, 'deal_details', 'Details') : d.deal_details}
              </td>
              {editable && (
                <td className="mdt-rowactions">
                  <button className="mdt-link danger" onClick={() => remove(d)}>Delete</button>
                </td>
              )}
            </tr>
          ))}
          {adding && <tr className="mdt-editrow">{addCells()}</tr>}
          {sortedDeals.length === 0 && !adding && <tr><td colSpan={cols} className="mdt-empty">No deals yet.</td></tr>}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={3}>{sortedDeals.length} in pipeline</td>
            <td className="mdt-total" data-testid="deal-total">
              {money0(ticketTotal)}
            </td>
            <td colSpan={editable ? 4 : 3}></td>
          </tr>
        </tfoot>
      </table>

      {editable && !adding && (
        <button className="mdt-add" onClick={startAdd}>+ Add deal</button>
      )}
    </div>
  );
}
